'use client';
import {useState} from 'react';
import {Input} from '@/shadcn/components/ui/input';
import {Button} from '@/shadcn/components/ui/button';
import {CheckCircle2} from 'lucide-react';
import {getTurnstileToken} from "@/app/actions/turnstile-client";
import {subscribeToNewsletter} from "@/app/actions/newsletter-actions";
import {optimisticManager} from "@/lib/optimistic-loading";

export default function NewsletterSmall() {
    const [email, setEmail] = useState('');
    const [submitted, setSubmitted] = useState(false);
    const [error, setError] = useState<null | undefined | string>(null);
    const [loading, setLoading] = useState(false);

    const handleSubmit = async (e: any) => {
        e.preventDefault();
        setError(null);

        if (email) {
            // Immediate optimistic success
            setSubmitted(true);
            setLoading(false);
            
            // Execute background newsletter subscription
            const taskId = `newsletter-small-${email}-${Date.now()}`;
            
            optimisticManager.executeOptimistic(
                taskId,
                { email, subscribed: true },
                async () => {
                    const token = await getTurnstileToken();
                    const result = await subscribeToNewsletter(email, true, token);
                    
                    if (!result.success) {
                        throw new Error(result?.message || 'Ein Fehler ist aufgetreten.');
                    }
                },
                {
                    notifyOnError: true,
                    taskName: 'Newsletter Small Subscription',
                    context: { 
                        email: email,
                        source: 'Newsletter Small Component'
                    }
                }
            );
        } else {
            setError('Bitte gib eine gültige E-Mail-Adresse ein.');
        }
    };

    return (
        <div className="lg:flex lg:items-center lg:justify-between">
            {submitted ? (
                <div className="flex items-center space-x-4 text-green-600">
                    <CheckCircle2 className="h-6 w-6" />
                    <p className="text-sm font-medium">
                        Vielen Dank! Du hast dich erfolgreich für unseren Newsletter angemeldet. Bitte überprüfe deine E-Mails, um die Anmeldung abzuschließen.
                    </p>
                </div>
            ) : (
                <>
                    <div>
                        <p className="text-xl font-semibold text-slate-900">Newsletter anmelden</p>
                        <p className="mt-2 text-sm text-slate-600">
                            Erhalte die neuesten Updates und exklusive Angebote direkt in deinen Posteingang.
                        </p>
                    </div>
                    <form onSubmit={handleSubmit} className="mt-6 sm:flex sm:max-w-md md:max-w-lg w-full  lg:mt-0">
                        <label htmlFor="email-address" className="sr-only">
                            Email address
                        </label>
                        <Input
                            id="email-address"
                            name="email-address"
                            type="email"
                            required
                            placeholder="Deine E-Mail eingeben"
                            autoComplete="email"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            className="mb-4 sm:mb-0 sm:grow text-base"
                        />
                        <div className="mt-4 sm:ml-4 sm:mt-0">
                            <Button
                                type="submit"
                                disabled={loading}
                                className="w-full sm:w-auto flex items-center justify-center rounded-md bg-amber-500 active:bg-amber-600 hover:bg-amber-600 px-3 py-2 text-sm font-semibold text-white"
                            >
                                {loading ? 'Anmeldung läuft...' : 'Abonnieren'}
                            </Button>
                        </div>
                    </form>
                    {error && <p className="mt-4 text-sm text-red-500">{error}</p>}
                </>
            )}
        </div>
    );
}
